/**
 * Echo Hunt -- the Story tab (P11, §8.3 + §8.4).
 *
 * One running story: who it follows, where it is, how suspicious the character has become, what worlds it
 * has been through, and every past round. Two actions live here that belong to nothing else:
 *
 *  1. **The override** (§8.3 "World change (override)") -- "rescue / I'm bored". It asks the NEXT round's
 *     weave for a way out; it deliberately does not move the character itself, because §8.3 has the
 *     character decide whether to go through the door. The panel says that plainly so the button is not
 *     mistaken for teleportation.
 *  2. **The suspicion meter** (§8.4) -- shown with the same wording the reveal and the settings panel use,
 *     plus whether applying it is even switched on. See `Story.ts`: the number is recorded either way, and
 *     the opt-out only stops it acting on the story.
 *
 * ## Why the character picker lives here
 *
 * §8.3's "orphan repair" (a story whose character was deleted from the pool) cannot be played at all until
 * a character is picked. `GetWeaveContext_Live` refuses with a message that names the Characters tab, so
 * this panel is where the refusal is actually resolvable -- showing the picker next to the story it repairs
 * is the difference between "the app is broken" and "press one of these buttons".
 *
 * ## Why the project takes no `sourceKind`
 *
 * It does not need one. The rounds are rendered as STORY history (their woven text and their score), never
 * as a hunt, and the reveal -- which does show labels -- is a separate component gated behind a submitted
 * round (§16.0).
 */
import React, {useEffect, useRef, useState} from "react";
import {observer_mgl} from "mobx-graphlink";
import {Button, CheckBox, Column, Row, Select, Text, TextArea} from "react-vcomponents";
import {ES, InfoButton, RunInAction_Set} from "web-vcore";
import {ScrollView} from "react-vscrollview";
import {ShowMessageBox} from "react-vmessagebox";
import {store} from "../../../Store/index.js";
import {EchoHuntTab} from "../../../Store/main/tools/echoHunt.js";
import {GetMyEchoProfile, GetSuspicionSettings} from "../../../EchoHunt/Profiles_Live.js";
import {GetCharacterPool_Live, StoryCharacterMissing} from "../../../EchoHunt/Characters_Live.js";
import {GetWorldPool_Live, GetRunningStories_Live, IsActiveStorySelectionStale_Live} from "../../../EchoHunt/Weave_Live.js";
import {
	AssignStoryCharacter_Live,
	CreateStory_Live,
	DescribeStorySuspicion_Live,
	EndStory_Live,
	GetActiveStory_Live,
	GetDoorState_Live,
	GetMyEchoStories_Live,
	GetStoryCharacter_Live,
	GetStoryPlayability_Live,
	GetStoryRounds_Live,
	GetStoryWorld_Live,
	GetStoryWorldHistory_Live,
	GetStoryWorldId_Live,
	GetStoryWorldName_Live,
	HasSuspicionAdjustment_Live,
	IsDoorOverrideRequested,
	IsStoryEndedManually,
	ReopenStory_Live,
	RequestDoorOverride_Live,
	ResetStorySuspicion_Live,
	SetActiveStory_Live,
	SetInterlude_Live,
	SetStorySuspicion_Live,
	SetStoryWorld_Live,
	SuspicionThresholdIsClamped_Live,
} from "../../../EchoHunt/Story_Live.js";
import {DescribeSuspicion, GetAccumulatedSuspicion, GetEffectiveSuspicion, GetSuspicionAdjustment, HasUsableSummary, IsSuspicionThresholdReached} from "../../../EchoHunt/Story.js";
import {EchoStory} from "../../../Server/DB/echoStories/@EchoStory.js";
import {EchoRound} from "../../../Server/DB/echoRounds/@EchoRound.js";
import {HasUnansweredDoor, SlotText} from "../../../EchoHunt/Round.js";
import {ProgressSpinner} from "../../../Utils/ReactComponents/ProgressSpinner.js";

export const StoryPanel = observer_mgl(()=>{
	const profile = GetMyEchoProfile();
	const uiState = store.main.tools.echoHunt;
	const stories = GetMyEchoStories_Live();
	const openStory = stories.find(a=>a.id == uiState.storyBeingEdited) ?? null;

	return (
		<ScrollView style={ES({flex: 1, padding: 10})} contentStyle={{display: "flex", flexDirection: "column"}}>
			<Row center style={{gap: 8, flexWrap: "wrap"}}>
				{/* the Back button replaces the page title, so it is genuinely at the top of the page it undoes --
				    the Content->Stories small-screen pattern */}
				{openStory != null
					? <Button text="Back" onClick={()=>RunInAction_Set(()=>{ uiState.storyBeingEdited = null; })}/>
					: <Text>Echo Hunt -- Story</Text>}
				{openStory != null && <Text style={{opacity: .8}}>{
					`${GetStoryCharacter_Live(openStory)?.name ?? "(character deleted)"} in ${GetStoryWorld_Live(openStory)?.name ?? "?"}`
				}</Text>}
				<InfoButton ml={5} text={[
					"§8.3: a story is ONE character's run. It carries the running summary between rounds, the world",
					"they are currently in, the history of worlds they have passed through, and the suspicion meter.",
					"",
					"Any number of stories may run at once. Exactly one is ACTIVE, and the Play tab builds its rounds into",
					"that one. Tick a story's box to make it active; click the story to open it.",
					"",
					"§8.3 doors: a round can end with a way out. Going through it moves the story to another",
					"world from your pool; staying leaves it where it is. The decision is offered on the reveal of the",
					"round that offered it.",
					"",
					"§8.3 override: 'Open a way out' asks the NEXT round to make one available. It does not move the",
					"character -- the door appears in the world's own terms and they still choose whether to use it.",
					"",
					"§8.4 suspicion: your correct echo finds raise the character's suspicion. Past the threshold they",
					"notice something is wrong, check, and wake up -- narrated in an interlude, which ends the story",
					"and the next one starts with a DIFFERENT character.",
					"",
					"Applying suspicion can be switched OFF on the Settings tab. The meter still records then, it just",
					"never interrupts or ends the story.",
				].join("\n")}/>
			</Row>

			{profile == null
				? <Text mt={10} style={{opacity: .7}}>Create an Echo Hunt profile (Settings tab) to run a story.</Text>
				: <StoryBody/>}
		</ScrollView>
	);
});

const StoryBody = observer_mgl(()=>{
	const uiState = store.main.tools.echoHunt;
	const active = GetActiveStory_Live();
	const stories = GetMyEchoStories_Live();

	/** which story the page is SHOWING -- view state, not the active story (see the store field's comment) */
	const openStory = stories.find(a=>a.id == uiState.storyBeingEdited) ?? null;

	// an id that no longer resolves (the story was deleted elsewhere) must not strand the user on a blank page
	if (uiState.storyBeingEdited != null && openStory == null) {
		RunInAction_Set(()=>{ uiState.storyBeingEdited = null; });
	}

	if (openStory != null) {
		return <StoryDetailBody story={openStory} isActive={openStory.id == active?.id}/>;
	}

	return <StoryListBody stories={stories} activeId={active?.id ?? null}/>;
});

/** The list: one row per story, and that is the whole page. Clicking a row opens it (below). */
const StoryListBody = observer_mgl((props: {stories: EchoStory[], activeId: string|null})=>{
	const {stories, activeId} = props;
	const uiState = store.main.tools.echoHunt;
	const [starting, setStarting] = useState(false);
	const [error, setError] = useState<string|null>(null);

	return (
		<Column mt={10} style={{gap: 10}}>
			<Row center style={{gap: 10, flexWrap: "wrap"}}>
				<Button text={starting ? "Starting…" : "Start a new story"} enabled={!starting} onClick={async()=>{
					setStarting(true);
					setError(null);
					try {
						const result = await CreateStory_Live();
						if (!result.ok) setError(result.error);
						// open it, so "start" lands you in the new run rather than leaving you to find its row
						else RunInAction_Set(()=>{ uiState.storyBeingEdited = result.story.id; });
					} catch (ex) {
						setError(`${ex}`);
					}
					setStarting(false);
				}}/>
				{starting && <ProgressSpinner/>}
				<PoolReadiness/>
			</Row>
			{error != null && <Text sel style={{color: "red"}}>{error}</Text>}

			<StoryList stories={stories} activeId={activeId}/>
		</Column>
	);
});

/** One story, as the whole page. The Back button lives in the page header (`StoryPanel`), so this is only the
 * content -- the Content->Stories small-screen pattern, where the click replaces the page. */
const StoryDetailBody = observer_mgl((props: {story: EchoStory, isActive: boolean})=>{
	const {story, isActive} = props;

	return (
		<Column mt={10} style={{gap: 10}}>
			{!isActive &&
				<Text style={{fontSize: 11, opacity: .7}}>
					{story.ended == true
						? "This story has ended. It is kept as history -- go back and tick another story to make it active."
						: "This story is not active: the Play tab is building into a different one. Go back and tick its box to continue it."}
				</Text>}

			<ActiveStoryCard story={story}/>
			<SuspicionCard story={story}/>
			<DoorOverrideCard story={story}/>
			{StoryCharacterMissing(story) && <OrphanRepairCard story={story}/>}
			<EndStoryCard story={story}/>
			<StoryRounds story={story}/>
		</Column>
	);
});

/** The stories, one row each. Clicking a row OPENS it (replacing the page); the checkbox on the right is the
 * only control that changes which run the PLAY tab builds into. Ended stories are listed too, so this list
 * doubles as the history. */
const StoryList = observer_mgl((props: {stories: EchoStory[], activeId: string|null})=>{
	const {stories, activeId} = props;
	const uiState = store.main.tools.echoHunt;
	const [busyId, setBusyId] = useState<string|null>(null);
	const [error, setError] = useState<string|null>(null);
	const stale = IsActiveStorySelectionStale_Live();

	if (stories.length == 0) {
		return <Text style={{opacity: .7, fontSize: 12}}>
			No stories yet. Starting one draws a character and a world from your pools -- free, unlike building a round.
		</Text>;
	}

	return (
		<Column style={{gap: 4}}>
			<Row center style={{gap: 8, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold"}}>Stories</Text>
				<Text style={{fontSize: 11, opacity: .55}}>{`${stories.length} total`}</Text>
				<InfoButton text={[
					"Click a story to open it.",
					"",
					"Tick the box on the right to make it ACTIVE: the Play tab builds its rounds into that one, and only one",
					"story can be active at a time. Opening a story does not change which one is active.",
					"",
					"Ended stories stay in this list, so it doubles as the history.",
				].join("\n")}/>
			</Row>

			{stale &&
				<Text style={{fontSize: 11, color: "orange"}}>
					{"The story you last made active has ended, so the newest running one is shown as active instead."}
				</Text>}

			<ScrollView style={{maxHeight: 400}} contentStyle={{display: "flex", flexDirection: "column"}}>
				<Column style={{gap: 3}}>
					{stories.map(story=>{
						const character = GetStoryCharacter_Live(story);
						const world = GetStoryWorld_Live(story);
						const rounds = GetStoryRounds_Live(story.id);
						const roundCount = rounds.length;
						// `GetStoryRounds_Live` is oldest-first, so the newest round is the last one. Shown beside the
						// creation date because two stories created a week apart can be far more or less recently
						// played than each other, and the creation date alone reads as "last activity".
						const latestRound = rounds.length > 0 ? rounds[rounds.length - 1] : null;
						const isActive = story.id == activeId;
						const isEnded = story.ended == true;

						return (
							<Row key={story.id} center style={{
								gap: 8,
								padding: "6px 8px",
								borderRadius: 5,
								cursor: "pointer",
								// the ACTIVE row is tinted -- the only state this list shows, since the open row is
								// no longer visible once a click replaces the page
								background: isActive ? "rgba(120,255,150,.10)" : "rgba(255,255,255,.04)",
							}} onClick={()=>RunInAction_Set(()=>{ uiState.storyBeingEdited = story.id; })}>
								<Text style={{fontSize: 12}}>
									{`${character?.name ?? "(character deleted)"} in ${world?.name ?? "?"}`}
								</Text>
								<Text style={{fontSize: 11, opacity: .55}}>
									{`${roundCount} round(s) · ${DescribeSuspicion(GetEffectiveSuspicion(story), GetSuspicionSettings())}`}
								</Text>
								<Text style={{fontSize: 11, opacity: .45}}>
									{latestRound != null
										? `started ${new Date(story.createdAt).toLocaleDateString()} · last round ${new Date(latestRound.createdAt).toLocaleDateString()}`
										: `started ${new Date(story.createdAt).toLocaleDateString()} · no rounds yet`}
								</Text>
								{isEnded && <Text style={{fontSize: 11, opacity: .5}}>(ended)</Text>}
								{/* the active toggle sits at the RIGHT edge, and its click must not also open the row */}
								<Row style={{flex: 1, justifyContent: "flex-end"}} onClick={e=>e.stopPropagation()}>
									{busyId == story.id
										? <ProgressSpinner size={16}/>
										: <CheckBox value={isActive} enabled={!isEnded} title={isEnded
											? "An ended story cannot be made active."
											: "Build the Play tab's rounds into this story."}
										onChange={async(value)=>{
											if (!value || isEnded) return;
											setBusyId(story.id);
											setError(null);
											try {
												if (!await SetActiveStory_Live(story.id)) {
													setError("That story can no longer be made active (it may have just ended).");
												}
											} catch (ex) {
												setError(`${ex}`);
											}
											setBusyId(null);
										}}/>}
								</Row>
							</Row>
						);
					})}
				</Column>
			</ScrollView>

			{error != null && <Text sel style={{color: "red", fontSize: 12}}>{error}</Text>}
		</Column>
	);
});

/** Whether the two pools a story needs exist, with a link to whichever is empty. Shown BEFORE Start is
 * pressed: the button's error is accurate but the remedy is on another tab. */
const PoolReadiness = observer_mgl(()=>{
	const worlds = GetWorldPool_Live();
	const characters = GetCharacterPool_Live();

	if (worlds.length > 0 && characters.length > 0) {
		return (
			<Text style={{fontSize: 11, opacity: .55}}>
				{`Ready: ${worlds.length} world(s), ${characters.length} character(s).`}
			</Text>
		);
	}

	return (
		<Row center style={{gap: 10, flexWrap: "wrap"}}>
			{worlds.length == 0 &&
				<Row center style={{gap: 6}}>
					<Text style={{fontSize: 12, color: "orange"}}>No worlds yet.</Text>
					<Button text="Add a world" onClick={()=>RunInAction_Set(()=>{ store.main.tools.echoHunt.tab = EchoHuntTab.worlds; })}/>
				</Row>}
			{characters.length == 0 &&
				<Row center style={{gap: 6}}>
					<Text style={{fontSize: 12, color: "orange"}}>No characters yet.</Text>
					<Button text="Add a character" onClick={()=>RunInAction_Set(()=>{ store.main.tools.echoHunt.tab = EchoHuntTab.characters; })}/>
				</Row>}
		</Row>
	);
});

// the story itself
// ==========
const ActiveStoryCard = observer_mgl((props: {story: EchoStory})=>{
	const {story} = props;
	const world = GetStoryWorld_Live(story);
	const worldName = GetStoryWorldName_Live(story);
	const character = GetStoryCharacter_Live(story);
	const playability = GetStoryPlayability_Live(story);
	const history = GetStoryWorldHistory_Live(story);
	const rounds = GetStoryRounds_Live(story.id);

	return (
		<Column sel style={{padding: 10, borderRadius: 5, background: "rgba(255,255,255,.05)", gap: 6}}>
			<Row center style={{gap: 12, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold", fontSize: 15}}>
					{character != null ? character.name : "(no character)"}
				</Text>
				<Text style={{opacity: .75}}>{`in ${worldName ?? (world == null ? "(no world recorded)" : "(world no longer in your pool)")}`}</Text>
				<Text style={{fontSize: 11, opacity: .55}}>{`${rounds.length} round(s) so far`}</Text>
			</Row>

			{character != null &&
				<Text style={{fontSize: 12, opacity: .75}}>
					{character.tagline.length > 0 ? character.tagline : "—"}
					{character.personalityNotes.length > 0 ? ` · ${character.personalityNotes}` : ""}
				</Text>}

			{world != null &&
				<Text style={{fontSize: 12, opacity: .75}}>{world.bible}</Text>}

			{/* editable, not just displayed: a random draw the user disagrees with must not leave "end the story"
			    as the only remedy. Editing the ENTRY itself stays on the Characters/Worlds tabs (§8.7). */}
			<StoryPickers story={story}/>

			{!playability.playable &&
				<Text style={{fontSize: 12, color: "orange"}}>
					{playability.reason == "ended"
						? "This story has ended. Start a new one to continue playing."
						: "This story's character is no longer in your pool, so it cannot be played until you pick one."}
				</Text>}

			{HasUsableSummary(story.summary) &&
				<Column mt={2} style={{gap: 2}}>
					<Text style={{fontSize: 11, opacity: .55}}>Story so far (carried between rounds)</Text>
					<Text style={{fontSize: 12, opacity: .85}}>{story.summary}</Text>
				</Column>}

			{history.length > 0 &&
				<Column mt={2} style={{gap: 2}}>
					<Text style={{fontSize: 11, opacity: .55}}>Worlds visited</Text>
					<Row center style={{gap: 6, flexWrap: "wrap"}}>
						{history.map((visit, index)=>
							<Text key={`${visit.worldId}-${index}`} style={{fontSize: 12, opacity: index == history.length - 1 ? .95 : .6}}>
								{`${index > 0 ? "→ " : ""}${visit.worldName ?? visit.worldId}`}
							</Text>)}
					</Row>
				</Column>}
		</Column>
	);
});

/** "Who this story follows" and "where it is", as pickers. Both write straight through, like the other Echo
 * Hunt settings panels, and are DISABLED with a pointer to the tab that fills them when a pool is empty. */
const StoryPickers = observer_mgl((props: {story: EchoStory})=>{
	const {story} = props;
	const worlds = GetWorldPool_Live();
	const characters = GetCharacterPool_Live();
	const doorInPlay = GetDoorState_Live(story);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string|null>(null);

	const goToTab = (tab: EchoHuntTab)=>{
		RunInAction_Set(()=>{ store.main.tools.echoHunt.tab = tab; });
	};

	const worldOptions = worlds.map(entry=>({name: entry.name, value: entry.id}));
	const characterOptions = characters.map(entry=>({name: entry.name, value: entry.id}));
	/** An orphaned story (its character was deleted) has no id to select, and without a placeholder the
	 * dropdown would highlight the FIRST character as though assigned. The placeholder cannot be re-selected. */
	const noCharacterOption = {name: "(no character -- pick one)", value: ""};
	const characterSelection = characters.some(entry=>entry.id == story.characterId)
		? story.characterId!
		: noCharacterOption.value;

	const write = async(action: ()=>Promise<boolean>, failureMessage: string)=>{
		setBusy(true);
		setError(null);
		try {
			if (!await action()) setError(failureMessage);
		} catch (ex) {
			setError(`${ex}`);
		}
		setBusy(false);
	};

	return (
		<Column style={{gap: 4}}>
			<Row center style={{gap: 8, flexWrap: "wrap"}}>
				<Text style={{fontSize: 12, width: 110, opacity: .7}}>Follows:</Text>
				{characters.length === 0
					? <Row center style={{gap: 8, flexWrap: "wrap"}}>
						<Text style={{fontSize: 12, color: "orange"}}>Your character pool is empty.</Text>
						<Button text="Add a character" onClick={()=>goToTab(EchoHuntTab.characters)}/>
					</Row>
					: <Select displayType="dropdown" style={{minWidth: 200}}
						options={[noCharacterOption, ...characterOptions]}
						value={characterSelection}
						onChange={(value: string)=>{
							// the placeholder is a label, not a choice: selecting it must not clear the assignment
							if (value.length == 0) return;
							write(()=>AssignStoryCharacter_Live(story.id, value), "That character is no longer in your pool.");
						}}/>}
				{characters.length > 0 &&
					<Text style={{fontSize: 11, opacity: .55}}>edit the entry itself on the Characters tab</Text>}
			</Row>

			<Row center style={{gap: 8, flexWrap: "wrap"}}>
				<Text style={{fontSize: 12, width: 110, opacity: .7}}>Set in:</Text>
				{worlds.length === 0
					? <Row center style={{gap: 8, flexWrap: "wrap"}}>
						<Text style={{fontSize: 12, color: "orange"}}>Your world pool is empty.</Text>
						<Button text="Add a world" onClick={()=>goToTab(EchoHuntTab.worlds)}/>
					</Row>
					: <Select displayType="dropdown" style={{minWidth: 200}}
						options={worldOptions}
						enabled={!doorInPlay}
						value={GetStoryWorldId_Live(story) ?? ""}
						onChange={(value: string)=>write(
							async()=>{
								const result = await SetStoryWorld_Live(story.id, value);
								if (!result.ok) { setError(result.error); return false; }
								return true;
							},
							"That world is no longer in your pool.")}/>}
				{/* a door in play means the story's next move is already spoken for (§11.5 W5) */}
				{doorInPlay
					? <Text style={{fontSize: 11, color: "orange"}}>a door is in play -- answer or play it out first</Text>
					: worlds.length > 0 && <Text style={{fontSize: 11, opacity: .55}}>moves the story now; a door moves it in the fiction</Text>}
			</Row>

			{busy && <ProgressSpinner/>}
			{error != null && <Text sel style={{color: "red", fontSize: 12}}>{error}</Text>}
		</Column>
	);
});

/** The by-hand suspicion slider (debounced: a drag fires one change per pixel). Moving it stores an OFFSET
 * from the story's accumulated value, so `Reset` can always restore exactly that value. */
const SuspicionSlider = observer_mgl((props: {story: EchoStory, value: number})=>{
	const {story, value} = props;
	const [draft, setDraft] = useState<number|null>(null);
	const [saving, setSaving] = useState(false);
	const pendingRef = useRef<ReturnType<typeof setTimeout>|null>(null);
	const shown = draft ?? value;
	const adjusted = HasSuspicionAdjustment_Live(story);
	const accumulated = GetAccumulatedSuspicion(story);

	// a pending write must not outlive the component (switching stories mid-drag)
	useEffect(()=>()=>{
		if (pendingRef.current != null) clearTimeout(pendingRef.current);
	}, []);

	return (
		<Column style={{gap: 2}}>
			<Row center style={{gap: 8, flexWrap: "wrap"}}>
				<Text style={{fontSize: 11, opacity: .6, width: 110}}>{"Set by hand:"}</Text>
				<input type="range" min={0} max={100} step={5} value={Math.round(shown * 100)}
					style={{flex: 1, minWidth: 120, cursor: "pointer"}}
					disabled={story.ended == true}
					onChange={e=>{
						const next = parseInt(e.target.value) / 100;
						setDraft(next);
						setSaving(true);
						// per-change debounce: a fast drag coalesces into one write, and stopping mid-drag still saves
						if (pendingRef.current != null) clearTimeout(pendingRef.current);
						pendingRef.current = setTimeout(async()=>{
							await SetStorySuspicion_Live(story.id, next);
							setDraft(null);
							setSaving(false);
						}, 400);
					}}/>
				{adjusted && story.ended != true &&
					<Button text="Reset" title="Drop your adjustment, so the meter goes back to what the story itself accumulated."
						onClick={async()=>{
							setSaving(true);
							await ResetStorySuspicion_Live(story.id);
							setDraft(null);
							setSaving(false);
						}}/>}
				{saving && <ProgressSpinner size={14}/>}
			</Row>
			<Text style={{fontSize: 11, opacity: .55, paddingLeft: 118}}>
				{story.ended == true
					? "This story has ended, so the meter is no longer used."
					: adjusted
						? `You adjusted this by ${FormatSuspicionSigned(GetSuspicionAdjustment(story))}; the story itself has accumulated ${Math.round(accumulated * 100)}%. Press Reset to go back to that.`
						: "Moving this stores an OFFSET from the story's own value, so Reset always returns to it."}
			</Text>
		</Column>
	);
});

const SuspicionCard = observer_mgl((props: {story: EchoStory})=>{
	const {story} = props;
	const settings = GetSuspicionSettings();
	const value = GetEffectiveSuspicion(story);
	const clamped = SuspicionThresholdIsClamped_Live();
	const nearThreshold = IsSuspicionThresholdReached(story, settings);

	return (
		<Column style={{padding: 10, borderRadius: 5, background: "rgba(255,255,255,.04)", gap: 4}}>
			<Row center style={{gap: 10, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold"}}>Suspicion</Text>
				<Text style={{fontSize: 12, opacity: .85}}>{DescribeStorySuspicion_Live(story)}</Text>
				<InfoButton text={[
					"§8.4: correct echo finds raise this; the character's dawning realisation is modelled by it. Past the",
					"threshold they notice, check, and wake up -- which ends the story.",
					"",
					settings.applied
						? "Applying suspicion is ON, so reaching the threshold ends this story."
						: "Applying suspicion is OFF, so this number will never end the story. It is still recorded, so you can switch the feature back on later without losing it.",
					"",
					"The meter is computed from your past rounds' scores only, so it cannot tell you anything about the",
					"round you are about to play.",
				].join("\n")}/>
			</Row>

			<Row center style={{gap: 4}}>
				{/* a plain bar rather than a styled component: it is one number, and it should read at a glance */}
				<Row style={{height: 8, flex: 1, borderRadius: 4, background: "rgba(255,255,255,.12)", overflow: "hidden"}}>
					<Row style={{width: `${value * 100}%`, height: 8, background: nearThreshold ? "tomato" : "rgba(120,200,255,.75)"}}/>
				</Row>
				{settings.applied &&
					<Text style={{fontSize: 11, opacity: .5}}>{`threshold ${Math.round(settings.threshold * 100)}%`}</Text>}
			</Row>

			<Text style={{fontSize: 11, opacity: .6}}>
				{settings.applied
					? `+${Math.round(settings.gainPerHit * 100)}% per echo found, -${Math.round(settings.lossPerMiss * 100)}% per echo missed.`
					: `Recorded only. +${Math.round(settings.gainPerHit * 100)}% per echo found, -${Math.round(settings.lossPerMiss * 100)}% per miss.`}
			</Text>

			{/* manual adjustment: the meter only moves on submitted rounds, so without this there is no way to
			    nudge a run toward (or away from) its ending without playing rounds for it */}
			<SuspicionSlider story={story} value={value}/>

			{clamped &&
				<Text style={{fontSize: 11, color: "orange"}}>
					{`Your saved threshold is below the ${Math.round(0.05 * 100)}% floor, so the meter is using the floor instead.`}
				</Text>}

			{!settings.applied &&
				<Text style={{fontSize: 11, opacity: .6}}>
					Switch this on (or off) on the Settings tab, under "Suspicion".
				</Text>}

			{story.lucidInterlude != null && story.lucidInterlude.length > 0 &&
				<InterludeEditor storyId={story.id} interlude={story.lucidInterlude}/>}
		</Column>
	);
});

/** The narrated ending, editable (§8.4). The narration is the one artifact of a story a user may well want
 * to fix -- and re-running the model to change one sentence costs tokens and produces a DIFFERENT ending,
 * which is the one thing you cannot do when you are editing an ending. */
const InterludeEditor = observer_mgl((props: {storyId: string, interlude: string})=>{
	const {storyId} = props;
	const [editing, setEditing] = useState(false);
	const [draft, setDraft] = useState(props.interlude);
	const [busy, setBusy] = useState(false);

	if (!editing) {
		return (
			<Column mt={4} style={{gap: 2}}>
				<Row center style={{gap: 8}}>
					<Text style={{fontSize: 11, opacity: .55}}>The interlude (the story's ending)</Text>
					<Button text="Edit" onClick={()=>{
						setDraft(props.interlude);
						setEditing(true);
					}}/>
				</Row>
				<Text style={{fontSize: 12, opacity: .9}}>{props.interlude}</Text>
			</Column>
		);
	}

	return (
		<Column mt={4} style={{gap: 4}}>
			<Text style={{fontSize: 11, opacity: .55}}>The interlude (the story's ending)</Text>
			<TextArea autoSize={true} instant style={{fontSize: 12}} value={draft} onChange={setDraft}/>
			<Row center style={{gap: 8}}>
				<Button text={busy ? "Saving…" : "Save"} enabled={!busy} onClick={async()=>{
					setBusy(true);
					await SetInterlude_Live(storyId, draft);
					setBusy(false);
					setEditing(false);
				}}/>
				<Button text="Cancel" enabled={!busy} onClick={()=>setEditing(false)}/>
				{busy && <ProgressSpinner/>}
			</Row>
		</Column>
	);
});

// the override (§8.3)
// ==========

const DoorOverrideCard = observer_mgl((props: {story: EchoStory})=>{
	const {story} = props;
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string|null>(null);
	const requested = IsDoorOverrideRequested(story);
	const doorInPlay = GetDoorState_Live(story);

	return (
		<Column style={{padding: 10, borderRadius: 5, background: "rgba(255,255,255,.04)", gap: 5}}>
			<Row center style={{gap: 10, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold"}}>Way out</Text>
				<Button text={requested ? "Requested" : "Open a way out next round"} enabled={!requested && !busy} onClick={async()=>{
					setBusy(true);
					setError(null);
					try {
						const ok = await RequestDoorOverride_Live(story.id);
						if (!ok) setError("This story has ended, so it cannot be moved.");
					} catch (ex) {
						setError(`${ex}`);
					}
					setBusy(false);
				}}/>
				{busy && <ProgressSpinner/>}
				<InfoButton text={[
					"§8.3 override: for when you are bored of this world, or the character is stuck and you want out.",
					"",
					"It does not move them. The next round is written so that a way out is AVAILABLE in the world's",
					"own terms -- a door, a threshold, a route somewhere else -- and the character still decides whether",
					"to use it. That keeps their choice meaningful, which is the whole reason the frame has a character",
					"with strong values.",
					"",
					"The request is one-shot: it applies to the next round and then clears.",
				].join("\n")}/>
			</Row>
			<Text style={{fontSize: 11, opacity: .6}}>
				{requested
					? "The next round will present a way out. Going through it is still the character's decision."
					: "Asks the next round to make a way out available. The character still chooses whether to take it."}
			</Text>
			{doorInPlay &&
				<Text style={{fontSize: 11, color: "orange"}}>
					{doorInPlay.awaitingCharacter
						? "A door is already waiting for the character in the next round. Play that round before asking for another."
						: "A door is waiting for your answer on the latest round's reveal (the Play tab). Answer it before asking for another."}
				</Text>}
			{error != null && <Text sel style={{color: "red", fontSize: 12}}>{error}</Text>}
		</Column>
	);
});

// orphan repair (§8.3, §8.7)
// ==========

const OrphanRepairCard = observer_mgl((props: {story: EchoStory})=>{
	const {story} = props;
	const pool = GetCharacterPool_Live();
	const [selected, setSelected] = useState<string|n>(null);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string|null>(null);

	const options = pool.map(entry=>({name: `${entry.name}${entry.tagline.length > 0 ? ` -- ${entry.tagline}` : ""}`, value: entry.id}));
	const chosen = selected ?? options[0]?.value ?? null;

	return (
		<Column style={{padding: 10, borderRadius: 5, background: "rgba(255,120,80,.10)", gap: 5}}>
			<Text style={{fontWeight: "bold", color: "orange"}}>This story has no character</Text>
			<Text style={{fontSize: 12, opacity: .85}}>
				The character it followed was deleted from your pool. Pick another and the story continues -- its summary,
				world and suspicion all stay as they are.
			</Text>
			{pool.length == 0
				? <Text style={{fontSize: 12, color: "orange"}}>
					Your pool is empty. Add a character on the Characters tab first.
				</Text>
				: <Row center style={{gap: 8, flexWrap: "wrap"}}>
					<Select displayType="dropdown" options={options} value={chosen} onChange={setSelected}
						style={{minWidth: 200}}/>
					<Button text={busy ? "Saving…" : "Follow this character"} enabled={!busy && chosen != null} onClick={async()=>{
						if (chosen == null) return;
						setBusy(true);
						setError(null);
						try {
							const ok = await AssignStoryCharacter_Live(story.id, chosen);
							if (!ok) setError("That character is no longer in your pool.");
						} catch (ex) {
							setError(`${ex}`);
						}
						setBusy(false);
					}}/>
					{busy && <ProgressSpinner/>}
				</Row>}
			{error != null && <Text sel style={{color: "red", fontSize: 12}}>{error}</Text>}
		</Column>
	);
});

// ending / re-opening a story
// ==========

/** End (with a confirmation) and -- for a MANUAL ending only -- re-open. The undo is withheld from a LUCID
 * ending, whose narrated interlude the player has already read: continuing would contradict the fiction
 * rather than undo a click. */
const EndStoryCard = observer_mgl((props: {story: EchoStory})=>{
	const {story} = props;
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string|null>(null);
	const otherRunning = GetRunningStories_Live().filter(a=>a.id != story.id);
	const canReopen = IsStoryEndedManually(story);

	const reopen = async()=>{
		setBusy(true);
		setError(null);
		try {
			if (!await ReopenStory_Live(story.id)) setError("This story cannot be re-opened.");
		} catch (ex) {
			setError(`${ex}`);
		}
		setBusy(false);
	};

	const end = ()=>{
		ShowMessageBox({
			title: "End this story", cancelButton: true,
			message: [
				`End the run following ${GetStoryCharacter_Live(story)?.name ?? "this character"}?`,
				"",
				"Its summary, suspicion and world history are all kept, and you can re-open it from this page if you",
				"ended it by accident. Nothing is deleted.",
				"",
				otherRunning.length > 0
					? "Your next running story becomes active."
					: "Nothing else is running, so you will need to start a new story to keep playing.",
			].join("\n"),
			onOK: async()=>{
				setBusy(true);
				setError(null);
				try {
					await EndStory_Live(story.id);
					// ending no longer implies starting one; with other runs alive, switch to the newest of those,
					// or the tab (and the Play tab) would keep pointing at a run the user just retired
					const next = otherRunning[0] ?? null;
					if (next != null) await SetActiveStory_Live(next.id);
				} catch (ex) {
					setError(`${ex}`);
				}
				setBusy(false);
			},
		});
	};

	if (story.ended == true) {
		return (
			<Column style={{gap: 4, padding: 10, borderRadius: 5, background: "rgba(255,255,255,.04)"}}>
				<Row center style={{gap: 10, flexWrap: "wrap"}}>
					<Text style={{fontWeight: "bold"}}>This story has ended</Text>
					{canReopen
						? <>
							<Button text={busy ? "Re-opening…" : "Re-open this story"} enabled={!busy} onClick={reopen}/>
							{busy && <ProgressSpinner/>}
							<Text style={{fontSize: 11, opacity: .6}}>
								It was ended by hand, so nothing in the story happened to close it. Re-opening resumes where it
								left off -- suspicion is left exactly as it was, so if it is already past the threshold the next
								round will end the run.
							</Text>
						</>
						: <Text style={{fontSize: 11, opacity: .6}}>
							{"The character noticed and woke up, which is narrated in the interlude above. Re-opening would contradict that ending, so this is final -- start a new story (it picks a different character by default)."}
						</Text>}
				</Row>
				{error != null && <Text sel style={{color: "red", fontSize: 12}}>{error}</Text>}
			</Column>
		);
	}

	return (
		<Column style={{gap: 4, padding: 10, borderRadius: 5, background: "rgba(255,255,255,.04)"}}>
			<Row center style={{gap: 10, flexWrap: "wrap"}}>
				<Button text={busy ? "Ending…" : "End this story"} enabled={!busy} onClick={end}/>
				{busy && <ProgressSpinner/>}
				<Text style={{fontSize: 11, opacity: .6}}>
					{otherRunning.length > 0
						? "Closes this run and switches to your next running story. Nothing is deleted, and it can be re-opened."
						: "Closes this run. Nothing is deleted, and it can be re-opened -- though nothing else would be running to play meanwhile."}
				</Text>
			</Row>
			{error != null && <Text sel style={{color: "red", fontSize: 12}}>{error}</Text>}
		</Column>
	);
});

// rounds (§16.2 P11 "Story history view: past rounds, read-only")
// ==========

const StoryRounds = observer_mgl((props: {story: EchoStory})=>{
	const {story} = props;
	const rounds = GetStoryRounds_Live(story.id);
	const [expanded, setExpanded] = useState<string|null>(null);

	if (rounds.length == 0) {
		return <Text style={{opacity: .6, fontSize: 12}}>No rounds yet -- build one on the Play tab.</Text>;
	}

	return (
		<Column style={{gap: 4}}>
			<Text style={{fontWeight: "bold"}}>Rounds</Text>
			{rounds.map((round, index)=>
				<RoundRow key={round.id} round={round} index={index}
					expanded={expanded == round.id}
					onToggle={()=>setExpanded(expanded == round.id ? null : round.id)}/>)}
		</Column>
	);
});

const RoundRow = observer_mgl((props: {round: EchoRound, index: number, expanded: boolean, onToggle: ()=>void})=>{
	const {round, index, expanded, onToggle} = props;
	const slots = round.slots ?? [];
	const revealed = round.revealedAt != null;
	const doorUnanswered = HasUnansweredDoor(round);

	// §11.5 W7: the round now records where its door LED, so the log can distinguish a door move from a
	// manual one without reading the story's history.
	const resolvedName = round.doorResolvedWorldId != null
		? GetWorldPool_Live().find(a=>a.id == round.doorResolvedWorldId)?.name ?? round.doorResolvedWorldId
		: null;

	return (
		<Column style={{padding: 8, borderRadius: 5, background: "rgba(255,255,255,.04)", gap: 3}}>
			<Row center style={{gap: 10, flexWrap: "wrap"}}>
				<Text style={{fontWeight: "bold", fontSize: 12}}>{`#${index + 1}`}</Text>
				<Text style={{fontSize: 11, opacity: .6}}>{new Date(round.createdAt).toLocaleString()}</Text>
				<Text style={{fontSize: 11, opacity: .6}}>{`${slots.length} bullets`}</Text>
				<Text style={{fontSize: 11, opacity: revealed ? .7 : .45}}>{revealed ? "revealed" : "not played yet"}</Text>
				{doorUnanswered && <Text style={{fontSize: 11, color: "orange"}}>a door is waiting for your answer</Text>}
				{round.doorVerdict != null &&
					<Text style={{fontSize: 11, opacity: .6}}>
						{round.doorVerdict == "accept" ? "you accepted the door" : "you declined the door"}
					</Text>}
				{round.doorDecision != null &&
					<Text style={{fontSize: 11, opacity: .6}}>
						{round.doorDecision == "taken"
							? `the character went through${resolvedName != null ? ` to ${resolvedName}` : ""}`
							: "the character left it alone"}
					</Text>}
				<Button text={expanded ? "Hide" : "Show"} onClick={onToggle}/>
			</Row>

			{round.doorOffer != null &&
				<Text style={{fontSize: 11, opacity: .6}}>{`door offered: ${round.doorOffer.description}`}</Text>}

			{expanded &&
				<Column mt={4} style={{gap: 3}}>
					{slots.map(slot=>
						<Text key={slot.slotId} style={{fontSize: 12, opacity: .85}}>{SlotText(slot)}</Text>)}
				</Column>}
		</Column>
	);
});

/** A signed percentage for the suspicion ADJUSTMENT, which can be negative (unlike a meter reading). */
function FormatSuspicionSigned(value: number): string {
	const percent = Math.round(value * 100);
	return `${percent > 0 ? "+" : ""}${percent}%`;
}
